import { DataFilter } from "interfaces";

import { DataFile } from "./data-files";
import { TimeSeries } from "./time-series";
import { AugmentData } from "./data-augment";

export interface MissingTWithDrop {
  column: string;
  treatment: "drop-rows" | "ignore";
}

export interface MissingTWithFill<T = string | number | Date> {
  column: string;
  treatment: "fill-value";
  value: T;
}

export interface MissingTWithSub<T = string | number | Date> {
  column: string;
  treatment: "substitute";
  value?: T;
  "substitute-params": {
    substitute: string;
    treatment: Omit<
      MissingTWithFill<T> | MissingTWithDrop,
      "column" | "substitute-params"
    >;
  };
}

export type MissingTreatment<T = string | number | Date> =
  | MissingTWithDrop
  | MissingTWithFill<T>
  | MissingTWithSub<T>;

export function missingTIsDrop<T>(
  treatment: MissingTreatment<T>
): treatment is MissingTWithDrop {
  return (
    treatment.treatment === "ignore" || treatment.treatment === "drop-rows"
  );
}

export function missingTIsFill<T>(
  treatment: MissingTreatment<T>
): treatment is MissingTWithFill<T> {
  return treatment.treatment === "fill-value";
}

export function missingTIsSubstitute<T>(
  treatment: MissingTreatment<T>
): treatment is MissingTWithSub<T> {
  return treatment.treatment === "substitute";
}

interface OutlierTIgnore {
  column: string;
  treatment: "ignore";
}

interface OutlierTDropString {
  column: string;
  treatment: "drop";
  range: string[];
}

interface OutlierTDropNumeric<T = number | Date> {
  column: string;
  treatment: "drop";
  range: [T, T];
}

export type OutlierTDrop<T = string | number | Date> =
  | OutlierTDropString
  | OutlierTDropNumeric<T>;

interface OutlierTClampString {
  column: string;
  treatment: "clamp";
  range: string[];
  replacement: string;
}

interface OutlierTClampNumeric<T = number | Date> {
  column: string;
  treatment: "clamp";
  range: [T, T];
  floor: T;
  ceiling: T;
}

type OutlierTClamp<T = string | number | Date> =
  | OutlierTClampString
  | OutlierTClampNumeric<T>;

export type OutlierTreatment<T = string | number | Date> =
  | OutlierTIgnore
  | OutlierTDrop<T>
  | OutlierTClamp<T>;

export function outlierTIsDrop<T>(
  treatment: OutlierTreatment<T>
): treatment is OutlierTDrop<T> {
  return treatment.treatment === "drop";
}

export function outlierTIsClampNumeric<T>(
  treatment: OutlierTreatment<T>
): treatment is OutlierTClampNumeric<T> {
  return (
    treatment.treatment === "clamp" &&
    (treatment as OutlierTClampNumeric<T>).floor !== undefined &&
    (treatment as OutlierTClampNumeric<T>).ceiling !== undefined
  );
}

export function outlierTIsClampChar<T>(
  treatment: OutlierTreatment<T>
): treatment is OutlierTClampString {
  return (
    treatment.treatment === "clamp" &&
    (treatment as OutlierTClampString).replacement !== undefined
  );
}

export interface StringTransform {
  column: string;
  treatment: "upper-case" | "lower-case" | "capitalize" | "split";
}

export declare namespace TransformData {
  export interface CleanupSpec {
    name: string;
    source_data: string;
    column_types: DataFile.UserDefinedTypes;
    columns_to_drop: string[];
    column_renamers: { [column: string]: string };
    outlier_treatment: OutlierTreatment[];
    missing_treatment: MissingTreatment[];
    string_transforms: StringTransform[];
  }

  export interface FilterSpec {
    name: string;
    source_data: string;
    column_types: DataFile.UserDefinedTypes;
    columns_to_be_dropped: string[];
    filters: DataFilter.Filter[];
  }

  export interface JoinSpec {
    source_data: string[];
    name: string;
    indexers: { [source: string]: string[] };
    column_types: { [source: string]: DataFile.UserDefinedTypes };
    columns_to_drop: { [source: string]: string[] };
    column_renamers: { [source: string]: { [column: string]: string } };
  }

  export interface StackSpec {
    name: string;
    source_data: string[];
    column_types: { [source: string]: DataFile.UserDefinedTypes };
    columns_to_drop: { [source: string]: string[] };
    column_renamers: { [source: string]: { [column: string]: string } };
  }

  export type NumAggregator =
    | "mean"
    | "median"
    | "sum"
    | "total"
    | "count"
    | "size"
    | "max"
    | "min"
    | "std"
    | "std-dev"
    | "mean-diff"
    | "median-diff"
    | "max-diff"
    | "min-diff"
    | "first"
    | "last";

  export type CatAggregator =
    | "count"
    | "size"
    | "unique-count"
    | "top-level"
    | "concentration"
    | "first"
    | "last"
    | "contents";

  export type Aggregator = NumAggregator | CatAggregator;

  export interface DataGroupedAggregatesSpec {
    source_data: string;
    name: string;
    grouping_columns: string[];
    aggregators: { [column: string]: (NumAggregator | CatAggregator)[] };
    sorting_column?: string;
    filters: DataFilter.Filter[];
    column_types: DataFile.UserDefinedTypes;
  }

  export interface DataConvertToTimeSeriesSpec {
    source_data: string;
    name: string;
    time_column: string;
    time_resolution?: TimeSeries.Resolution;
    aggregators: { [col: string]: TimeSeries.Aggregator[] };
    zero_size_interval_treatment: TimeSeries.ZeroSizeTreatment;
    grouping_columns?: string[];
    filters?: DataFilter.Filter[];
    column_types?: DataFile.UserDefinedTypes;
  }

  export interface DataAugmentSpec {
    name: string;
    source_data: string;
    column_types: DataFile.UserDefinedTypes;
    new_columns: AugmentData.NewColumnSpec[];
    columns_to_drop?: string[];
    missing_treatment?: MissingTreatment[];
  }

  export interface FlattenSpec {
    name: string;
    source_data: string;
    factor_variable: string;
    grouping_variables: string[];
    group_attributes?: string[];
    aggregators: {
      [column: string]: (
        | "count"
        | "mean"
        | "sum"
        | "proportion"
        | "min"
        | "max"
      )[];
    };
    column_types?: DataFile.UserDefinedTypes;
    filters?: DataFilter.Filter[];
  }

  export interface MacroSpec {
    name: string;
    source_data: { [key: string]: string };
    template: string;
  }
}
